import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type TabKey = 'today' | 'week' | 'interval' | 'discover';

type Step = {
  id: string;
  selector: string;
  title: string;
  description: string;
  preferredPlacement?: 'top' | 'bottom';
  gotoTab?: TabKey;
};

type TourGuideProps = {
  onClose: () => void;
  gotoTab: (tab: TabKey) => void;
};

export const TourGuide: React.FC<TourGuideProps> = ({ onClose, gotoTab }) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [current, setCurrent] = useState(0);
  const [active, setActive] = useState(true);
  const [steps] = useState<Step[]>([
    {
      id: 'tabs',
      selector: '[data-tour="top-tabs"]',
      title: t('tour.steps.tabs.title'),
      description: t('tour.steps.tabs.desc'),
      preferredPlacement: 'bottom'
    },
    {
      id: 'today-timeline',
      selector: '[data-tour="today-timeline"]',
      title: t('tour.steps.today.title'),
      description: t('tour.steps.today.desc'),
      preferredPlacement: 'bottom',
      gotoTab: 'today'
    },
    {
      id: 'week-view',
      selector: '[data-tour="week-view"]',
      title: t('tour.steps.week.title'),
      description: t('tour.steps.week.desc'),
      preferredPlacement: 'bottom',
      gotoTab: 'week'
    },
    {
      id: 'week-excel',
      selector: '[data-tour="week-excel"]',
      title: t('tour.steps.week_excel.title'),
      description: t('tour.steps.week_excel.desc'),
      preferredPlacement: 'bottom',
      gotoTab: 'week'
    },
    {
      id: 'discover',
      selector: '[data-tour="tab-discover"]',
      title: t('tour.steps.discover.title'),
      description: t('tour.steps.discover.desc'),
      preferredPlacement: 'top',
      gotoTab: 'discover'
    }
  ]);

  const getTargetRect = (selector: string): DOMRect | null => {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return null;
    return el.getBoundingClientRect();
  };

  const positionElements = () => {
    const step = steps[current];
    if (!step) return;

    const rect = getTargetRect(step.selector);
    const highlight = highlightRef.current;
    const tooltip = tooltipRef.current;
    if (!rect || !highlight || !tooltip) return;

    const padding = 8;
    const left = rect.left - padding + window.scrollX;
    const top = rect.top - padding + window.scrollY;
    const width = rect.width + padding * 2;
    const height = rect.height + padding * 2;

    highlight.style.left = `${left}px`;
    highlight.style.top = `${top}px`;
    highlight.style.width = `${width}px`;
    highlight.style.height = `${height}px`;

    const tooltipWidth = 340; // min width
    const tooltipHeight = 180; // approx
    let ttLeft = left + width / 2 - tooltipWidth / 2;
    ttLeft = Math.max(16 + window.scrollX, Math.min(ttLeft, window.scrollX + window.innerWidth - tooltipWidth - 16));

    let ttTop: number;
    if (step.preferredPlacement === 'top') {
      ttTop = top - tooltipHeight - 12;
      if (ttTop < window.scrollY + 16) {
        ttTop = top + height + 12;
      }
    } else {
      ttTop = top + height + 12;
      if (ttTop + tooltipHeight > window.scrollY + window.innerHeight - 16) {
        ttTop = top - tooltipHeight - 12;
      }
    }

    tooltip.style.left = `${ttLeft}px`;
    tooltip.style.top = `${ttTop}px`;
  };

  const showStep = (index: number) => {
    const step = steps[index];
    if (!step) return;

    if (step.gotoTab) {
      gotoTab(step.gotoTab);
    }

    let attempts = 0;
    const tryPosition = () => {
      attempts++;
      const rect = getTargetRect(step.selector);
      if (rect) {
        positionElements();
      } else if (attempts < 20) {
        setTimeout(tryPosition, 80);
      }
    };
    tryPosition();
  };

  useLayoutEffect(() => {
    if (!active) return;
    showStep(current);
    const onResize = () => positionElements();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, active]);

  const next = () => {
    if (current >= steps.length - 1) {
      gotoTab('today');
      onClose();
      setActive(false);
      return;
    }
    setCurrent((c) => c + 1);
  };

  const skip = () => {
    onClose();
    setActive(false);
  };

  if (!active) return null;

  return (
    <div className="fixed inset-0 z-[1000]">
      {/* Dim overlay */}
      <div ref={overlayRef} className="fixed inset-0 bg-black/60" />

      {/* Highlight box with big box-shadow to create hole */}
      <div
        ref={highlightRef}
        className="absolute border-2 border-indigo-400 rounded-xl shadow-[0_0_0_2000px_rgba(0,0,0,0.6)] transition-all duration-300 ease-out"
        style={{ pointerEvents: 'none' }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute bg-white rounded-2xl p-4 min-w-[320px] max-w-[360px] shadow-2xl transition-all"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-[13px] font-semibold">
            {current + 1}
          </div>
          <div className="text-[16px] font-semibold text-gray-900">{steps[current].title}</div>
        </div>
        <div className="text-[15px] text-gray-600 leading-relaxed mb-3">
          {steps[current].description}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={skip}
            className="px-3 py-2 text-[15px] text-gray-500 hover:text-gray-700"
          >
            {t('tour.skip')}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all ${i === current ? 'w-6 bg-gradient-to-r from-indigo-400 to-purple-500' : 'w-2 bg-gray-300'}`}
              />
            ))}
          </div>
          <button
            onClick={next}
            className="ml-3 px-4 py-2 text-[15px] font-semibold text-white rounded-md bg-gradient-to-r from-indigo-500 to-purple-500 hover:brightness-110 shadow-md"
          >
            {current === steps.length - 1 ? t('tour.done') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourGuide;

